import React, { useState } from 'react';
import {
  Actions,
  Box,
  Button,
  Field,
  Form,
  Leadtext,
  Link,
  Loading,
  RangeSelector,
  SelectField,
  Toggle,
  ToggleGroup,
} from '@ktag/styleguide/dist/react';
import { Attributes, JobPortalScreen, SearchParams } from 'jobs.types';
import { Trans, useTranslation } from 'react-i18next';
import getOnChangeAboFilter from 'javascripts/getOnChangeAboFilter';
import {
  ARBEITSORT_ATTRIBUTE_ID,
  RESET_ATTRIBUTE_ID,
  RUBRIK_ATTRIBUTE_ID,
  STELLART_ATTRIBUTE_ID,
} from '../../constants';
import AboToggleGroup from './AboToggleGroup';
import getAboBody, { FormData } from './getAboBody';
import AboTogglesAdditional from './AboTogglesAdditional';

const EMAIL_PATTERN =
  '^([a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)+)$';

interface Props {
  setScreen: (screen: JobPortalScreen) => void;
  filter: { search: SearchParams; jobAbo: SearchParams };
  attributes: Attributes;
  setFilter: (filter: { search: SearchParams; jobAbo: SearchParams }) => void;
  api: string;
  mocked: boolean;
  privacyUrl: string;
}

const Abo = ({
  setScreen,
  filter,
  attributes,
  setFilter,
  api,
  mocked,
  privacyUrl,
}: Props) => {
  const { t } = useTranslation('jobs');
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isCreated, setIsCreated] = useState(false);

  const locations = attributes[ARBEITSORT_ATTRIBUTE_ID]?.values
    ?.filter(({ id }) => {
      return !(id === RESET_ATTRIBUTE_ID);
    })
    .map((location) => {
      return {
        label: location.name,
        value: location.id,
      };
    });

  if (!attributes) return <Loading />;

  const onSelectLocations = (value: string[]) => {
    const newFilter = { ...filter };
    newFilter.jobAbo[ARBEITSORT_ATTRIBUTE_ID] = value;
    setFilter(newFilter);
  };

  const pensumRange = {
    min: parseInt(filter.jobAbo?.['sza_pensum.min']?.[0], 10) || 0,
    max: parseInt(filter.jobAbo?.['sza_pensum.max']?.[0], 10) || 100,
  };

  const setPensumMin = (value: number) => {
    const newFilter = { ...filter };
    newFilter.jobAbo['sza_pensum.min'] = [value.toString()];
    setFilter(newFilter);
  };

  const setPensumMax = (value: number) => {
    const newFilter = { ...filter };
    newFilter.jobAbo['sza_pensum.max'] = [value.toString()];
    setFilter(newFilter);
  };

  const onSubmit = async (e: FormData) => {
    setIsLoading(true);
    setHasError(false);
    setIsCreated(false);
    const url = mocked ? `${api}/abos.json` : `${api}abos`;
    const method = mocked ? 'GET' : 'PUT';
    const body = mocked ? null : JSON.stringify(getAboBody(e, filter.jobAbo));
    const response = await fetch(url, {
      method,
      body,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (response.ok) {
      setIsLoading(false);
      setIsCreated(true);
    } else {
      setHasError(true);
      setIsLoading(false);
      setIsCreated(false);
    }
  };

  return (
    <div className="abo">
      <Leadtext>{t('abo_leadtext')}</Leadtext>
      <h2 className="h2 abo__title">{t('abo_criteria')}</h2>
      <Form onSubmit={onSubmit}>
        {({ isValid }) => {
          return (
            <>
              <Field
                label={t('abo_search')}
                placeholder={t('abo_search_placeholder')}
                name="query"
                initialValue={filter.jobAbo.q}
              />
              {locations?.length && (
                <SelectField
                  label={t('abo_location')}
                  name="location"
                  options={locations}
                  multiple
                  clearable
                  value={filter.jobAbo?.[ARBEITSORT_ATTRIBUTE_ID]}
                  onChange={({ target }) => {
                    onSelectLocations(target.value as string[]);
                  }}
                  onInputChange={() => {}}
                />
              )}
              <div className="abo__filters">
                <AboToggleGroup
                  attributes={attributes}
                  filter={filter}
                  onChange={getOnChangeAboFilter({ setFilter, filter })}
                  attributeId={RUBRIK_ATTRIBUTE_ID}
                />
                <div>
                  <AboToggleGroup
                    attributes={attributes}
                    filter={filter}
                    onChange={getOnChangeAboFilter({ setFilter, filter })}
                    attributeId={STELLART_ATTRIBUTE_ID}
                  />
                  <AboTogglesAdditional
                    attributes={attributes}
                    filter={filter}
                    onChange={getOnChangeAboFilter({ setFilter, filter })}
                  />
                  <div className="abo__pensum">
                    <h3 className="h3">{t('filters_pensum')}</h3>
                    <RangeSelector
                      name="pensum"
                      sliderRange={pensumRange}
                      unit="%"
                      rangeStep={5}
                      setRangeMin={setPensumMin}
                      setRangeMax={setPensumMax}
                      valueVisible
                    />
                  </div>
                </div>
              </div>
              <h2 className="h2">{t('abo_your_mail')}</h2>
              <Field
                label={t('abo_name')}
                required
                placeholder={t('abo_name_placeholder')}
                name="aboName"
              />
              <Field
                label={t('abo_email')}
                required
                name="email"
                pattern={EMAIL_PATTERN}
                placeholder={t('abo_email_placeholder')}
              />
              <ToggleGroup name="accept-terms" required label={t('abo_terms_label')}>
                <Toggle
                  name="terms"
                  value="on"
                  label={
                    <Trans t={t} i18nKey="abo_terms_text">
                      <span />
                      <Link url={privacyUrl} openNewWindow />
                      <span />
                    </Trans>
                  }
                />
              </ToggleGroup>
              {isCreated && (
                <Box
                  type="positive"
                  additionalClassNames="abo__box"
                  heading={t('abo_success_title')}
                  footer={
                    <Button onClick={() => setScreen('jobsearch')}>
                      {t('abo_back')}
                    </Button>
                  }
                >
                  {t('abo_success_text')}
                </Box>
              )}
              {hasError && (
                <Box
                  type="negative"
                  additionalClassNames="abo__box"
                  heading={t('abo_error_title')}
                >
                  {t('abo_error_text')}
                </Box>
              )}
              {!isCreated && (
                <Actions label={t('abo_create')} disabled={!isValid} loading={isLoading}>
                  <Actions.Right>
                    <Button onClick={() => setScreen('jobsearch')}>
                      {t('abo_abort')}
                    </Button>
                  </Actions.Right>
                </Actions>
              )}
            </>
          );
        }}
      </Form>
    </div>
  );
};

export default Abo;
